import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { BotTyping } from 'components/BotTyping'
import { messagesSelectors } from 'store/messages'
import { memo, useEffect, useRef, useState } from 'react'
import { TicketReminderRow } from 'components/ui/TicketReminderRow'
import { Spinner } from '@/components/ui/spinner'
import {
  MessageScrollerProvider,
  MessageScroller,
  MessageScrollerViewport,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerButton,
} from '@/components/ui/message-scroller'
import { getChatMessages } from 'api/chats'
import { ChatMessages } from 'components/chats/ChatMessages'
import { DefaultQuestions } from 'components/DefaultQuestions'
import { settingsSelectors } from 'store/settings'
import { store } from '@/store'
import { useGetSetting } from 'api/settings'
import { useChatWebSocket } from 'hooks/useChatWebSocket'
import { useAgenticBootstrap } from 'hooks/useAgenticBootstrap'

const hasCachedMessages = (chatId?: string) =>
  !!chatId && (store.getState().messageState.messagesByChat[chatId]?.length ?? 0) > 0

type ChatDetailProps = {
  /** The chat page passes false: its sidebar already owns the user WS channel, and a double subscribe would duplicate handlers. */
  subscribeUserChannel?: boolean
  /** Called after every message load (chat switches included); the chat page uses it to flush the hero's pending question. */
  onMessagesLoaded?: () => void
}

const ChatDetailComponent = ({ subscribeUserChannel = true, onMessagesLoaded }: ChatDetailProps) => {
  const { t } = useTranslation()
  const { numberOfQuestionsBeforeAskTicketOpen } = useGetSetting()
  const chatId = useSelector(messagesSelectors.chatId)
  const currentChatBotId = useSelector(messagesSelectors.currentChatBotId)
  const busy = useSelector(messagesSelectors.currentChatBusy)
  const [loading, setLoading] = useState(() => !hasCachedMessages(chatId))
  const currentUserId = useSelector(settingsSelectors.userId)

  const onMessagesLoadedRef = useRef(onMessagesLoaded)
  onMessagesLoadedRef.current = onMessagesLoaded

  useChatWebSocket({
    chatId,
    userId: subscribeUserChannel ? currentUserId : undefined,
    currentChatBotId,
    currentUserId,
    numberOfQuestionsBeforeAskTicketOpen,
  })

  useAgenticBootstrap()

  useEffect(() => {
    let cancelled = false
    if (chatId) {
      setLoading(!hasCachedMessages(chatId))
      getChatMessages(chatId).then(() => {
        if (cancelled) return
        setLoading(false)
        onMessagesLoadedRef.current?.()
      })
    }
    return () => {
      cancelled = true
    }
  }, [chatId])

  if (loading) {
    return (
      <div className="flex min-h-0 w-full flex-1 items-center justify-center self-start">
        <Spinner />
      </div>
    )
  }

  return (
    <MessageScrollerProvider autoScroll={true}>
      <MessageScroller className="min-h-0 w-full flex-1 self-start">
        <MessageScrollerViewport aria-label={t('chat')} className="scrollbar-hidden">
          <MessageScrollerContent className="gap-3 p-3" aria-busy={busy} aria-relevant="additions text">
            <ChatMessages />
            <MessageScrollerItem messageId="suggested-questions" className="empty:hidden">
              <DefaultQuestions />
            </MessageScrollerItem>
            <MessageScrollerItem messageId="bot-typing" className="empty:hidden">
              <BotTyping />
            </MessageScrollerItem>
            <MessageScrollerItem messageId="ticket-reminder" className="empty:hidden">
              <TicketReminderRow />
            </MessageScrollerItem>
          </MessageScrollerContent>
        </MessageScrollerViewport>
        <MessageScrollerButton />
      </MessageScroller>
    </MessageScrollerProvider>
  )
}

export const ChatDetail = memo(ChatDetailComponent)
